<template>
  <div class="rich-text-renderer">
    <!-- 使用 v-html 渲染经过 DOMPurify 清理后的内容 -->
    <div class="rich-text-content" v-html="sanitizedContent"></div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import DOMPurify from 'dompurify';

// 定义 props
const props = defineProps({
  content: {
    type: String,
    default: ''
  }
});

// 计算属性，用于清理和提供内容
const sanitizedContent = computed(() => {
  if (!props.content) return '';
  // 使用 DOMPurify 清理 HTML，防止 XSS 攻击
  // ALLOWED_ATTR 可能需要根据你的编辑器输出进行调整，确保 style 和 class 被允许
  return DOMPurify.sanitize(props.content, {
    ALLOWED_ATTR: ['style', 'class', 'src', 'alt', 'width', 'height', 'aspect-ratio']
  });
});
</script>

<style scoped>
/* 容器样式，可以定义一些全局的富文本样式，比如字体、行高等 */
.rich-text-renderer {
  width: 100%;
  line-height: 1.6;
}

/* 
  核心样式：使用 :deep() 穿透 scoped 限制，为富文本内容定义样式
  我们的目标是忠实还原编辑器的布局，所以规则要具体且不冲突
*/

/* --- 1. 处理图片块 (<figure>) 的对齐 --- */

/* 默认图片块 (左对齐或不浮动) */
.rich-text-content :deep(figure.image) {
  /* 默认情况下，figure 是块级元素，我们不希望它有特殊行为 */
  display: inline-block; /* 或者 block，取决于你希望如何与文本交互 */
  margin: 1em 0; /* 给图片上下留出一些空间 */
  text-align: center; /* 让 figure 内部的 img 居中 */
  float: none; /* 确保默认不浮动 */
}

/* 左对齐图片块 */
.rich-text-content :deep(figure.image-style-align-left) {
  float: left;
  margin-right: 1em; /* 在图片右侧留出空间，让文字环绕 */
  margin-top: 0.5em;
  margin-bottom: 0.5em;
}

/* 右对齐图片块 (这是你例子中需要的) */
.rich-text-content :deep(figure.image-style-align-right) {
  float: right;
  margin-left: 1em; /* 在图片左侧留出空间，让文字环绕 */
  margin-top: 0.5em;
  margin-bottom: 0.5em;
}

/* 居中对齐图片块 */
.rich-text-content :deep(figure.image-style-align-center) {
  display: block;
  float: none;
  margin: 1em auto; /* 上下留边，左右自动实现居中 */
  text-align: center; /* 确保 figure 内部的 img 也居中 */
}

/* --- 2. 处理图片本身 (<img>) 的样式 --- */

/* 确保所有图片都是响应式的，不会超出容器 */
.rich-text-content :deep(img) {
  max-width: 100%;
  height: auto;
  border: 0; /* 移除默认边框 */
}

/* --- 3. 处理其他富文本元素 (可选，但推荐) --- */

/* 段落、标题等基本样式 */
.rich-text-content :deep(p),
.rich-text-content :deep(h1),
.rich-text-content :deep(h2),
.rich-text-content :deep(h3),
.rich-text-content :deep(h4),
.rich-text-content :deep(h5),
.rich-text-content :deep(h6) {
  margin-top: 0;
  margin-bottom: 1em;
  /* 重置一些默认样式，保持一致性 */
}

/* 清除浮动，防止浮动图片影响后续布局 */
.rich-text-content::after {
  content: "";
  display: table;
  clear: both;
}
</style>
